import { GoogleMapsRequirements } from '../../../types/search.types';

export type WebsiteStatus = 'any' | 'has' | 'none';

export const RATING_POSITION_MIN = 0;
export const RATING_POSITION_MAX = 41;

export const ratingPositionToValue = (position: number): number => {
  if (position <= RATING_POSITION_MIN) return 0;
  return Number((0.9 + Math.min(position, RATING_POSITION_MAX) / 10).toFixed(1));
};

export const ratingValueToPosition = (value: number): number => {
  if (value <= 0) return RATING_POSITION_MIN;
  return Math.max(1, Math.min(RATING_POSITION_MAX, Math.round((value - 0.9) * 10)));
};

export const formatRatingValue = (position: number): string => {
  const value = ratingPositionToValue(position);
  return value === 0 ? 'No rating' : value.toFixed(1);
};

export const formatRatingRange = (minPosition: number, maxPosition: number): string => {
  if (minPosition === RATING_POSITION_MIN && maxPosition === RATING_POSITION_MAX) {
    return 'Any rating';
  }
  if (minPosition === RATING_POSITION_MIN && maxPosition === RATING_POSITION_MIN) {
    return 'No rating only';
  }
  return `${formatRatingValue(minPosition)}–${formatRatingValue(maxPosition)}`;
};

export const buildLeadRequirements = (
  websiteStatus: WebsiteStatus,
  minRatingPosition: number,
  maxRatingPosition: number,
): GoogleMapsRequirements | undefined => {
  const requirements: GoogleMapsRequirements = {};

  if (websiteStatus === 'has') requirements.has_website = true;
  if (websiteStatus === 'none') requirements.has_website = false;

  const ratingIsFiltered = !(
    minRatingPosition === RATING_POSITION_MIN && maxRatingPosition === RATING_POSITION_MAX
  );
  if (ratingIsFiltered) {
    requirements.min_review_rating = ratingPositionToValue(minRatingPosition);
    requirements.max_review_rating = ratingPositionToValue(maxRatingPosition);
  }

  return Object.keys(requirements).length > 0 ? requirements : undefined;
};

export const formatWebsiteStatus = (status: WebsiteStatus): string => {
  if (status === 'has') return 'Has a website';
  if (status === 'none') return 'No website';
  return 'Any website status';
};

export type GoogleAdsSignal = 'detected_recently' | 'no_ad_tracking';

export const formatGoogleAdsSignal = (signal: GoogleAdsSignal | undefined): string | undefined => {
  if (signal === 'detected_recently') return 'Ads prioritized';
  if (signal === 'no_ad_tracking') return 'Non-advertisers prioritized';
  return undefined;
};
